// Generated by Claude Code
import type { ReactNode, Ref } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CardModal } from '#components/primitives/components/card-modal.tsx'
import { ConfirmModal } from '#components/primitives/components/confirm-modal.tsx'
import { FormErrorAlert } from '#components/primitives/components/form-error-alert.tsx'

export interface UpsertDrawerShellProps {
  isOpen: boolean
  /** Drawer title text (wrapped in `ynt-drawer-title`). */
  title: string
  /**
   * Footer content — typically a `FormFooterActions`. Pass a function to receive the
   * shell's guarded `requestClose`, so the footer Cancel routes through the same
   * unsaved-changes confirm as the X / Escape / Android-back.
   */
  footer: ReactNode | ((requestClose: () => void) => ReactNode)
  /** Form body. Rendered inside the scrollable `<form>`, after the error/warning block. */
  children: ReactNode
  /** Final close handler (runs after any unsaved-changes confirm is accepted). */
  onClose: () => void
  /** Ref for the scrollable `<form>` — attach for required-field scroll-into-view. */
  formRef?: Ref<HTMLFormElement>
  /** Fully gate the body behind a loading message when true. */
  loading?: boolean
  /** Loading message (defaults to `shared.common.loading`). */
  loadingLabel?: ReactNode
  /** Non-blocking banner rendered above the error block (e.g. a "loading existing…" note). */
  banner?: ReactNode
  /** Already-translated submit error. */
  errorMessage?: ReactNode | null
  /** Server-supplied reason shown under `errorMessage`. */
  errorDetail?: string | null
  /** Already-translated warnings. */
  warnings?: ReactNode[]
  /** Optional drawer width, e.g. `520px`. */
  drawerWidth?: string
  closeOnBackdropClick?: boolean
  /** Set false to leave Escape to a dialog the drawer opens over itself. */
  closeOnEscape?: boolean
  /**
   * When true, closing (X / Escape / Android-back / footer Cancel) prompts the
   * shared unsaved-changes confirm instead of closing immediately. The caller computes
   * this (e.g. `state.isDirty && !state.submitting`).
   */
  hasUnsavedChanges: boolean
}

/**
 * The shared scaffold for the operations create/edit drawers: a right-side
 * `CardModal` drawer with the `ynt-drawer-title` / `ynt-drawer-card` chrome, a
 * scrollable `<form>` gate, the shared {@link FormErrorAlert} block, and an
 * optional unsaved-changes confirm on close. Routes every close path (X, Escape,
 * Android-back) through one `requestClose`, so the close/back-stack guard and
 * validation scaffolding cannot drift between drawers.
 */
export function UpsertDrawerShell({
  isOpen,
  title,
  footer,
  children,
  onClose,
  formRef,
  loading = false,
  loadingLabel,
  banner,
  errorMessage,
  errorDetail,
  warnings,
  drawerWidth,
  closeOnBackdropClick = false,
  closeOnEscape = true,
  hasUnsavedChanges,
}: UpsertDrawerShellProps) {
  const { t } = useTranslation('shared')
  const [confirmOpen, setConfirmOpen] = useState(false)

  const requestClose = () => {
    if (hasUnsavedChanges) {
      setConfirmOpen(true)
      return
    }
    onClose()
  }

  const footerNode = typeof footer === 'function' ? footer(requestClose) : footer

  return (
    <>
      <CardModal
        isOpen={isOpen}
        title={<span className="ynt-drawer-title">{title}</span>}
        aria-label={title}
        variant="drawer"
        drawerSide="end"
        drawerWidth={drawerWidth}
        onClose={requestClose}
        footer={footerNode}
        cardClassName="ynt-drawer-card"
        closeOnBackdropClick={closeOnBackdropClick}
        closeOnEscape={closeOnEscape}
      >
        {loading ? (
          <div className="text-secondary py-3">{loadingLabel ?? t('common.loading')}</div>
        ) : (
          <form ref={formRef} onSubmit={(e) => e.preventDefault()} noValidate>
            {banner}
            <FormErrorAlert message={errorMessage} detail={errorDetail} warnings={warnings} />
            {children}
          </form>
        )}
      </CardModal>
      <ConfirmModal
        isOpen={confirmOpen}
        title={t('common.unsavedTitle')}
        message={t('common.unsavedMessage')}
        confirmLabel={t('actions.close')}
        cancelLabel={t('actions.cancel')}
        genericErrorLabel=""
        variant="primary"
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => { onClose() }}
      />
    </>
  )
}

export default UpsertDrawerShell
